
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import SavePanel from "../../../Base/SavePanel.vue";
import ChallengeBox from "../../../Base/ChallengeBox.vue";
import PoiChallengeFxTable from "../Tables/PoiChallengeFxTable.vue";
import { ChallengeData } from "@/models/Base/ChallengeData";
import { ManhuntMapPoi, PUZZLE_TYPE } from "@/models/MapPois/ManHunt/ManhuntMapPoi";
import ImageBox from "../../../Base/ImageBox.vue";
import ImageSelector from "../../../Base/ImageSelector.vue";
import { cloneDeep, isEqual } from "lodash";
import { Game } from "@/models/Game";

@Component({
  components: {
    SavePanel,
    ChallengeBox,
    PoiChallengeFxTable,
    ImageBox,
    ImageSelector,
  },
})
class PoiChallenge extends Vue {
  @Prop({ default: null }) readonly mapPoi!: ManhuntMapPoi;
  @Prop({ default: null }) readonly gameData!: Game;
  @Prop({ default: false }) readonly isSavedOrDiscard!: boolean;
  @Prop({ default: true }) readonly isEditingLocked!: boolean;
  poiType = PUZZLE_TYPE.MULTI_CHOICE;
  mapPoiClone = {} as ManhuntMapPoi;
  showSavePanel = false;
  codebreakerText = "";
  answer1 = "";
  answer2 = "";
  answer3 = "";
  numericCodeBreaker = false;
  sliceDifficulty = 3;
  lockDifficulty = 1;
  lockPins = 3;
  sliceImg = "";
  answercorrect = "1";
  showImageSelectorBool = false;

  get getValues(): any[] {
    var options = [
      {
        value: PUZZLE_TYPE.MULTI_CHOICE,
        text: "Multiple Choice",
      },
      {
        value: PUZZLE_TYPE.CBREAKER,
        text: "Code Breaker",
      },
      {
        value: PUZZLE_TYPE.SLICE,
        text: "Slice9",
      },
      {
        value: PUZZLE_TYPE.LPICK,
        text: "Lock Pick",
      },
    ];
    return options;
  }

  created(): void {
    this.mapPoiClone = cloneDeep(this.mapPoi);
    this.setDefaultValues();
  }

  get getImage(): string {
    return "";
  }

  get getImagePath(): string {
    return (
      "/gametype/" +
      this.gameData.gt_uid +
      "/seasons/" +
      this.gameData.seas_uid +
      "/img/poi/"
    );
  }
  get getAudioPath(): string {
    return (
      "/gametype/" +
      this.gameData.gt_uid +
      "/seasons/" +
      this.gameData.seas_uid +
      "/audio/poi/"
    );
  }
  get getVideoPath(): string {
    return (
      "/gametype/" +
      this.gameData.gt_uid +
      "/seasons/" +
      this.gameData.seas_uid +
      "/video/poi/"
    );
  }

  get getMax(): number {
    if (this.mapPoi.challenge.puzzle.type === PUZZLE_TYPE.MULTI_CHOICE) {
      return 2;
    } else {
      return 5;
    }
  }

  get getChallengeData(): ChallengeData {
    const challengeData = {} as ChallengeData;
    if (this.mapPoi.challenge.content !== undefined) {
      challengeData.text = this.mapPoi.challenge.content.txt.en;
      challengeData.image = this.mapPoi.challenge.content.h_thumb;
      if (this.mapPoi.challenge.content.audio !== undefined) {
        challengeData.audio = this.mapPoi.challenge.content.audio;
      } else {
        challengeData.audio = { en: "" };
      }
      if (this.mapPoi.challenge.content.video !== undefined) {
        challengeData.video = this.mapPoi.challenge.content.video;
      } else {
        challengeData.video = { en: "" };
      }
    } else {
      challengeData.text = "";
      challengeData.image = "";
      challengeData.audio = { en: "" };
      challengeData.video = { en: "" };
    }
    return challengeData;
  }

  get getQuestionData(): ChallengeData {
    const questionData = {} as ChallengeData;
    if (
      this.mapPoi.challenge.puzzle !== undefined &&
      this.mapPoi.challenge.puzzle.q !== undefined
    ) {
      questionData.text = this.mapPoi.challenge.puzzle.q.txt.en;
      questionData.image = this.mapPoi.challenge.puzzle.q.img;
      if (this.mapPoi.challenge.puzzle.q.audio !== undefined) {
        questionData.audio = this.mapPoi.challenge.puzzle.q.audio;
      } else {
        questionData.audio = { en: "" };
      }
      if (this.mapPoi.challenge.puzzle.q?.video !== undefined) {
        questionData.video = this.mapPoi.challenge.puzzle.q.video;
      } else {
        questionData.video = { en: "" };
      }
    } else {
      questionData.text = "";
      questionData.image = "";
      questionData.audio = { en: "" };
      questionData.video = { en: "" };
    }
    return questionData;
  }

  setDefaultValues(): void {
    if (this.mapPoi.challenge.puzzle.type === PUZZLE_TYPE.MULTI_CHOICE) {
      this.answer1 = this.mapPoi.challenge.puzzle.a?.a0.txt.en || "";
      this.answer2 = this.mapPoi.challenge.puzzle.a?.a1?.txt.en || "";
      this.answer3 = this.mapPoi.challenge.puzzle.a?.a2?.txt.en || "";
      if (this.mapPoi.challenge.puzzle.a?.a0?.correct) {
        this.answercorrect = "1";
      }
      if (this.mapPoi.challenge.puzzle.a?.a1?.correct) {
        this.answercorrect = "2";
      }
      if (this.mapPoi.challenge.puzzle.a?.a2?.correct) {
        this.answercorrect = "3";
      }
    }
    if (this.mapPoi.challenge.puzzle.type === PUZZLE_TYPE.CBREAKER) {
      this.codebreakerText = this.mapPoi.challenge.puzzle.a?.a0.txt.en || "";
      this.numericCodeBreaker =
        this.mapPoi.challenge.puzzle.q?.numeric || false;
    }
    if (this.mapPoi.challenge.puzzle.type === PUZZLE_TYPE.SLICE) {
      this.mapPoi.challenge.fail_timeout_s = 300;
      this.sliceImg = this.mapPoi.challenge.puzzle.q?.sliceImg || "";
      this.sliceDifficulty = this.mapPoi.challenge.puzzle.q?.difficulty || 3;
    }
    if (this.mapPoi.challenge.puzzle.type === PUZZLE_TYPE.LPICK) {
      this.mapPoi.challenge.fail_timeout_s = 300;
      this.lockDifficulty = this.mapPoi.challenge.puzzle.q?.difficulty || 1;
      this.lockPins = this.mapPoi.challenge.puzzle.q?.difficulty || 3;
    }
  }

  showImageSelector(): void {
    if (!this.isEditingLocked) {
      this.showImageSelectorBool = true;
    }
  }

  onChallengeDataChange(data: any): void {
    if (this.mapPoi.challenge.content === undefined) {
      this.mapPoi.challenge.content = {
        txt: { en: data.text },
        h_thumb: data.image,
        audio: data.audio,
        video: data.video,
        img: "",
      };
    }
    this.mapPoi.challenge.content.txt.en = data.text;
    this.mapPoi.challenge.content.h_thumb = data.image;
    this.mapPoi.challenge.content.audio = data.audio;
    this.mapPoi.challenge.content.video = data.video;
    this.onValueChanged();
  }
  onQuestionDataChange(data: any): void {
    this.mapPoi.challenge.puzzle["q"] = {
      txt: { en: data.text },
      img: data.image,
      audio: data.audio,
      video: data.video,
    };
    this.onValueChanged();
  }

  setValues(): void {
    try {
      if (this.mapPoi.challenge.puzzle.type === PUZZLE_TYPE.MULTI_CHOICE) {
        this.resetRadioButton();
        this.mapPoi.challenge.puzzle.a! = {
          a0: {
            correct: false,
            txt: { en: this.answer1 },
          },
          a1: {
            correct: false,
            txt: { en: this.answer2 },
          },
          a2: {
            correct: false,
            txt: { en: this.answer3 },
          },
        };
        if (this.answercorrect === "1") {
          this.mapPoi.challenge.puzzle.a!.a0.correct = true;
        } else if (this.answercorrect === "2") {
          this.mapPoi.challenge.puzzle.a!.a1!.correct = true;
        }
        if (this.answercorrect === "3") {
          this.mapPoi.challenge.puzzle.a!.a2!.correct = true;
        }
        delete this.mapPoi.challenge.puzzle.q?.numeric;
        delete this.mapPoi.challenge.puzzle.q?.pins;
        delete this.mapPoi.challenge.puzzle.q?.difficulty;
        delete this.mapPoi.challenge.puzzle.q?.sliceImg;
      }
      if (this.mapPoi.challenge.puzzle.type === PUZZLE_TYPE.CBREAKER) {
        this.mapPoi.challenge.puzzle.a! = {
          a0: {
            correct: true,
            txt: { en: this.codebreakerText },
          },
        };

        this.mapPoi.challenge.puzzle.q!.numeric = this.numericCodeBreaker;
        delete this.mapPoi.challenge.puzzle.a?.a1;
        delete this.mapPoi.challenge.puzzle.a?.a2;
        delete this.mapPoi.challenge.puzzle.q?.numeric;
        delete this.mapPoi.challenge.puzzle.q?.pins;
        delete this.mapPoi.challenge.puzzle.q?.difficulty;
        delete this.mapPoi.challenge.puzzle.q?.sliceImg;
      }
      if (this.mapPoi.challenge.puzzle.type === PUZZLE_TYPE.SLICE) {
        this.mapPoi.challenge.puzzle.q!.difficulty = this.sliceDifficulty;
        this.mapPoi.challenge.puzzle.q!.sliceImg = this.sliceImg;
        delete this.mapPoi.challenge.puzzle.a;
        delete this.mapPoi.challenge.puzzle.q?.numeric;
        delete this.mapPoi.challenge.puzzle.q?.pins;
      }
      if (this.mapPoi.challenge.puzzle.type === PUZZLE_TYPE.LPICK) {
        this.mapPoi.challenge.puzzle.q!.difficulty = this.lockDifficulty;
        this.mapPoi.challenge.puzzle.q!.pins = this.lockPins;
        delete this.mapPoi.challenge.puzzle.a;
        delete this.mapPoi.challenge.puzzle.q?.numeric;
        delete this.mapPoi.challenge.puzzle.q?.sliceImg;
      }
    } catch (e) {
      console.log(e);
    }
  }

  onValueChanged(): void {
    this.setValues();
    if (!isEqual(this.mapPoi, this.mapPoiClone)) {
      this.showSavePanel = true;
    } else {
      this.showSavePanel = false;
    }
    this.$emit("updateTempObject", this.mapPoi);
  }

  updateTempObject(challengeData: any): void {
    this.mapPoi.challenge.on_fail = challengeData.on_fail;
    this.mapPoi.challenge.on_incorrect = challengeData.on_incorrect;
    this.mapPoi.challenge.on_success = challengeData.on_success;
    this.onValueChanged();
  }

  handleCloseButtonPress(): void {
    this.showImageSelectorBool = false;
  }
  returnImagePath(path: string): void {
    this.sliceImg = this.getImagePath + "" + path;
    this.mapPoi.challenge.puzzle.q!.sliceImg = this.getImagePath + "" + path;
    this.showImageSelectorBool = false;
    this.onValueChanged();
  }

  resetRadioButton(): void {
    if (this.mapPoi.challenge.puzzle.a === undefined) {
      this.mapPoi.challenge.puzzle.a = {
        a0: {
          correct: false,
          txt: { en: "" },
        },
        a1: {
          correct: false,
          txt: { en: "" },
        },
        a2: {
          correct: false,
          txt: { en: "" },
        },
      };
    } else {
      if (this.mapPoi.challenge.puzzle.a!.a0.correct !== undefined)
        this.mapPoi.challenge.puzzle.a!.a0.correct = false;

      if (this.mapPoi.challenge.puzzle.a!.a1 !== undefined)
        this.mapPoi.challenge.puzzle.a!.a1!.correct = false;

      if (this.mapPoi.challenge.puzzle.a!.a2 !== undefined)
        this.mapPoi.challenge.puzzle.a!.a2!.correct = false;
    }
  }

  clearImage(): void {
    if (!this.isEditingLocked) {
      this.sliceImg = "";
      this.onValueChanged();
    }
  }

  @Watch("isSavedOrDiscard")
  onisSaveOrDiscardChanged(value: boolean): void {
    if (value) {
      this.showSavePanel = false;
    }
  }
  @Watch("mapPoi")
  onContentChange(value: ManhuntMapPoi): void {
    this.mapPoiClone = cloneDeep(value);
  }
}

export default PoiChallenge;
